
/**
 * Model cache eviction manager.
 *
 * Prevents OOM by evicting the least-recently-used GLTF scenes from
 * drei's `useGLTF` cache (backed by `suspend-react`) when the total
 * number of loaded model URLs exceeds MAX_CACHED_MODELS.
 *
 * Three layers of caching exist in the rendering pipeline:
 *   1. `suspenseCache` (useAssetUrl) — tiny URL strings, bounded below
 *   2. `suspend-react` (useGLTF) — **heavy**: full Three.js scene graphs
 *   3. `lodMap` (store) — tiny metadata
 *
 * Layer 2 is the OOM risk and the sole target of eviction here.
 */

import { useGLTF } from "@react-three/drei";
import { clearAssetUrlCache, evictAssetUrlByValue } from "../hooks/useAssetUrl";

/**
 * Maximum number of unique model URLs to keep in the GLTF cache.
 *
 * Rationale: scenes can have up to 500 objects sharing ~30-80 unique
 * model IDs. With 3-4 LOD tiers each, worst-case unique URLs ≈ 240.
 * A cap of 80 keeps the cache tight — enough for the current view's
 * active LOD tier plus a small buffer — while evicting stale tiers.
 */
const MAX_CACHED_MODELS = 80;

/**
 * LRU-ordered list of loaded model URLs.
 * Most recently used at the end, least recently used at the front.
 */
const lruOrder: string[] = [];

/**
 * URLs currently mounted in the scene (set of active useGLTF consumers).
 * These must NEVER be evicted while in use.
 */
const activeUrls = new Set<string>();

// ─── Internal helpers ────────────────────────────────────────

function touchLru(url: string): void {
  const idx = lruOrder.indexOf(url);
  if (idx !== -1) lruOrder.splice(idx, 1);
  lruOrder.push(url);
}

/** Evict the oldest non-active entry from all caches. */
function evictOldest(): boolean {
  for (let i = 0; i < lruOrder.length; i++) {
    const stale = lruOrder[i];
    if (activeUrls.has(stale)) continue;

    // Remove from LRU
    lruOrder.splice(i, 1);

    // Evict from drei's useGLTF cache (suspend-react)
    try {
      useGLTF.clear(stale);
    } catch {
      // Already cleared or URL never loaded — safe to ignore
    }

    // Evict matching suspenseCache entries (format: model:{id}:{lod})
    // whose resolved value equals the evicted URL.
    evictAssetUrlByValue(stale);

    return true;
  }
  return false;
}

function trimCache(): void {
  while (lruOrder.length > MAX_CACHED_MODELS) {
    const evicted = evictOldest();
    if (!evicted) break; // All remaining entries are active — nothing to evict
  }
}

// ─── Public API ──────────────────────────────────────────────

/**
 * Call when a model URL finishes loading (useGLTF returns a scene).
 * Records it in the LRU and trims the cache if needed.
 */
export function registerLoadedModel(url: string): void {
  touchLru(url);
  trimCache();
}

/**
 * Call when an ObjectRenderer mounts / its LOD tier resolves.
 * Marks the URL as actively in use (protects it from eviction).
 */
export function markModelActive(url: string): void {
  activeUrls.add(url);
  touchLru(url);
}

/**
 * Call when an ObjectRenderer unmounts or switches LOD tier.
 * Removes the "active" protection so the URL becomes evictable.
 */
export function markModelInactive(url: string): void {
  activeUrls.delete(url);
  // Do NOT evict immediately — it may be needed again shortly.
  // The LRU trim will handle eviction when the cap is exceeded.
}

/**
 * Evict all non-active model URLs from all caches.
 * Useful when navigating away from a scene or space.
 */
export function evictAllInactive(): void {
  // Walk backwards so splice indices stay valid
  for (let i = lruOrder.length - 1; i >= 0; i--) {
    const url = lruOrder[i];
    if (activeUrls.has(url)) continue;

    lruOrder.splice(i, 1);
    try {
      useGLTF.clear(url);
    } catch {
      // Already cleared
    }
  }
  clearAssetUrlCache();
}

/**
 * Full reset — evict everything, including active URLs.
 * Call when leaving the sandbox entirely.
 */
export function resetModelCache(): void {
  for (const url of lruOrder) {
    try {
      useGLTF.clear(url);
    } catch {
      // Already cleared
    }
  }
  lruOrder.length = 0;
  activeUrls.clear();
  clearAssetUrlCache();
}

/**
 * Read-only snapshot for debugging / ObjectInspector.
 */
export function getCacheStats(): {
  cached: number;
  active: number;
  cap: number;
} {
  return {
    cached: lruOrder.length,
    active: activeUrls.size,
    cap: MAX_CACHED_MODELS,
  };
}
